import { useState, useEffect, useRef } from 'react'

/**
 * @typedef {Object} WindowDimensions
 * @property {number} width - Current window width in pixels
 * @property {number} height - Current window height in pixels
 * @property {boolean} hasChanged - Indicates if dimensions changed from initial values
 */

/**
 * useWindowDimensions
 * Custom hook that returns current window dimensions and tracks if they have changed.
 * Updates on window resize with debouncing for performance.
 *
 * @function useWindowDimensions
 * @returns {WindowDimensions} Object containing width, height, and hasChanged flag
 *
 * @example
 * const { width, height, hasChanged } = useWindowDimensions()
 * if (hasChanged) {
 *   console.log(`Window resized to ${width}x${height}`)
 * }
 */
export default function useWindowDimensions() {
    /** @type {() => {width: number, height: number}} */
    const getDimensions = () => ({
        width: typeof window !== 'undefined' ? window.innerWidth : 0,
        height: typeof window !== 'undefined' ? window.innerHeight : 0
    })

    const initialDimensions = getDimensions()
    const [dimensions, setDimensions] = useState(initialDimensions)
    const [hasChanged, setHasChanged] = useState(false)

    /** @type {React.MutableRefObject<{width: number, height: number}>} */
    const initialDimensionsRef = useRef(initialDimensions)

    /** @type {React.MutableRefObject<number|null>} */
    const resizeTimer = useRef(null)

    useEffect(() => {
        if (typeof window === 'undefined') return undefined

        /** @function handleResize */
        const handleResize = () => {
            if (resizeTimer.current) {
                clearTimeout(resizeTimer.current)
            }

            resizeTimer.current = setTimeout(() => {
                const newDimensions = getDimensions()
                setDimensions(newDimensions)

                // Check if dimensions changed from initial values
                const changed =
                    newDimensions.width !== initialDimensionsRef.current.width ||
                    newDimensions.height !== initialDimensionsRef.current.height

                setHasChanged(changed)
            }, 100)
        }

        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
            if (resizeTimer.current) {
                clearTimeout(resizeTimer.current)
            }
        }
    }, [])

    return {
        width: dimensions.width,
        height: dimensions.height,
        hasChanged
    }
}
